import React, { useEffect, useRef } from 'react'
import { Button, FieldLabel, Icon } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { useRegisterManager, useSaleManager } from '../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  selectIsSaleUnfulfilled,
  selectIsSubmittedSaleEarly,
  selectSaleId,
  selectSaleNote,
  setHasUpdateNoteOnConfirmation,
  setSaleNote,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { NoteInput } from '../../shared/NoteInput'

const textAreaMinLines = 1
const textAreaMaxLines = 5

export interface NotesProps {
  onDeleted: () => void
  shouldFocus: boolean
}

export const Notes: React.FC<NotesProps> = ({
  onDeleted,
  shouldFocus,
}: NotesProps) => {
  const t = useT()
  const dispatch = useAppDispatch()
  const note = useAppSelector(selectSaleNote)
  const saleID = useAppSelector(selectSaleId)
  const isSubmittedEarly = useAppSelector(selectIsSubmittedSaleEarly)
  const isUnfulfilled = useAppSelector(selectIsSaleUnfulfilled)
  const saleManager = useSaleManager()
  const registerManager = useRegisterManager()

  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const handleNoteInput = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    dispatch(setSaleNote(event.target.value))

  const saveNote = (updatedNote: string) => {
    dispatch(setSaleNote(updatedNote))

    if (!saleID && isSubmittedEarly) {
      dispatch(setHasUpdateNoteOnConfirmation(true))
    }

    saleManager.saveSale()
  }

  const handleDeletion = () => {
    saveNote('')
    onDeleted()
  }

  useEffect(() => {
    if (!textAreaRef || !textAreaRef.current) {
      return
    }

    const element = textAreaRef.current
    // If we don't set the height to 0px, scrollHeight will not match the
    // content of the text area.
    // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight
    // This is going to be set to right side at the end of this function.
    element.style.height = '0px'
    const computedStyle = getComputedStyle(element)

    const scrollHeight = element.scrollHeight
    const paddingTop = parseInt(computedStyle.paddingTop, 10)
    const paddingBottom = parseInt(computedStyle.paddingBottom, 10)
    const lineHeight = parseInt(computedStyle.lineHeight, 10)

    const totalHeight = scrollHeight - paddingBottom - paddingTop
    const totalContentLines = Math.round(totalHeight / lineHeight)

    let targetLines = textAreaMinLines

    if (totalContentLines > textAreaMaxLines) {
      targetLines = textAreaMaxLines
    } else if (
      totalContentLines <= textAreaMaxLines &&
      totalContentLines > textAreaMinLines
    ) {
      targetLines = totalContentLines
    }

    element.style.height = `${
      paddingTop + paddingBottom + lineHeight * targetLines
    }px`
  }, [textAreaRef, note])

  const shouldPrintNoteOnReceipt = registerManager.getActiveRegister()
    .print_note_on_receipt

  const noteInputLabel = shouldPrintNoteOnReceipt
    ? t('Note', {
        _context: 'Sale note label for the sale note input',
      })
    : t('Staff Note', {
        _context:
          'Sale note label for the sale note input when it will not be printed on the receipt',
      })

  const noteInputSupplementaryLabel = shouldPrintNoteOnReceipt
    ? t('Customers will be able to see this note on their receipt', {
        _context: 'Supplementary label to the sale notes input',
      })
    : t('Customers will not see this note on their receipt', {
        _context: 'Supplementary label to the sale notes input',
      })

  return (
    <div className="sale-note sale-note-label-line">
      <FieldLabel
        label={noteInputLabel}
        supplementaryLabel={noteInputSupplementaryLabel}
      >
        <div className="add-action-line-container">
          <NoteInput
            ref={textAreaRef}
            placeholder={t('Enter a note about this sale', {
              _context: 'sale note placeholder',
            })}
            note={note}
            onInput={handleNoteInput}
            charLimit={2000}
            autoFocus={shouldFocus}
            onBlur={() => saveNote(note)}
          />
          {!isUnfulfilled && (
            <Button
              modifier="icon"
              variant="no"
              className="vd-ml1 sale-note-remove-button"
              onClick={handleDeletion}
              aria-label={t('Delete sale note', {
                _context: 'Aria label for the delete sale note trash icon',
              })}
              data-track="delete-sale-note"
            >
              <Icon icon="trash" />
            </Button>
          )}
        </div>
      </FieldLabel>
    </div>
  )
}
